<script>
import ConversationApi from 'dashboard/api/inbox/conversation';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  name: 'AddLeadModal',
  components: { NextButton },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    columns: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      contactName: '',
      phoneNumber: '',
      selectedInboxId: '',
      selectedAssigneeId: null,
      selectedColumnLabel: '',
      isSaving: false,
      errorMessage: '',
    };
  },
  computed: {
    inboxes() {
      return this.$store.getters['inboxes/getInboxes'];
    },
    agents() {
      return this.$store.getters['agents/getAgents'];
    },
    currentUser() {
      return this.$store.getters.getCurrentUser;
    },
    isFormValid() {
      return (
        this.contactName.trim().length > 0 &&
        this.phoneNumber.trim().length > 0 &&
        this.selectedInboxId !== '' &&
        this.selectedColumnLabel !== ''
      );
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.resetForm();
      }
    },
  },
  mounted() {
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('agents/get');
  },
  methods: {
    resetForm() {
      this.contactName = '';
      this.phoneNumber = '';
      this.errorMessage = '';
      this.isSaving = false;
      this.selectedAssigneeId = this.currentUser?.id || null;
      if (this.columns.length > 0) {
        this.selectedColumnLabel = this.columns[0].label;
      }
      if (this.inboxes.length > 0) {
        // Prefer whatsapp channel if available
        const whatsapp = this.inboxes.find(
          i => i.channel_type === 'Channel::Whatsapp'
        );
        this.selectedInboxId = whatsapp ? whatsapp.id : this.inboxes[0].id;
      }
    },
    onClose() {
      this.$emit('close');
      this.resetForm();
    },
    async saveLead() {
      this.isSaving = true;
      this.errorMessage = '';
      let contactId = null;

      try {
        // 1. Create or Find Contact
        try {
          // ensure phone number includes +, some apis demand it
          let formattedPhone = this.phoneNumber.trim();
          if (!formattedPhone.startsWith('+')) {
            formattedPhone = '+' + formattedPhone;
          }

          const contactResponse = await this.$store.dispatch(
            'contacts/create',
            {
              name: this.contactName,
              phoneNumber: formattedPhone,
            }
          );
          contactId = contactResponse.id;
        } catch (e) {
          // Contact already exists -> we gracefully extract the existing contact's ID
          if (e.data && e.data.id) {
            contactId = e.data.id;
          } else {
            throw e;
          }
        }

        // 2. Create Conversation
        const conversationPayload = {
          inbox_id: this.selectedInboxId,
          contact_id: contactId,
        };
        if (this.selectedAssigneeId) {
          conversationPayload.assignee_id = this.selectedAssigneeId;
        }

        const convResponse = await ConversationApi.create(conversationPayload);
        const conversationId = convResponse.data.id;

        // 3. Apply Label to Conversation
        await this.$store.dispatch('conversationLabels/update', {
          conversationId: conversationId,
          labels: [this.selectedColumnLabel],
        });

        useAlert('Lead adicionado com sucesso!');
        this.$emit('lead-added');
        this.onClose();

        // 4. Force a reload of the Kanban to display the newly created card
        this.$store.dispatch('fetchAllConversations');
      } catch (err) {
        useAlert('Erro ao adicionar o lead. Tente novamente.');
        this.errorMessage =
          err.message || 'Erro de comunicação com o servidor.';
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="flex flex-col h-full bg-white dark:bg-slate-900">
      <div
        class="px-6 py-4 border-b border-n-weak flex justify-between items-center"
      >
        <h2
          class="text-lg font-semibold m-0 text-slate-800 dark:text-slate-100"
        >
          Adicionar Novo Lead
        </h2>
      </div>

      <div class="flex-1 px-6 py-4 space-y-4">
        <!-- Name -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Nome do Contato <span class="text-red-500">*</span>
          </label>
          <input
            v-model="contactName"
            type="text"
            class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-surface-1 text-slate-900 dark:text-slate-100"
            placeholder="Ex: João da Silva"
          />
        </div>

        <!-- Phone -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Telefone / WhatsApp <span class="text-red-500">*</span>
          </label>
          <input
            v-model="phoneNumber"
            type="text"
            class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-surface-1 text-slate-900 dark:text-slate-100"
            placeholder="Ex: +5511999999999"
          />
        </div>

        <!-- Inbox Select -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Canal de Entrada (Inbox) <span class="text-red-500">*</span>
          </label>
          <select
            v-model="selectedInboxId"
            class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md bg-n-surface-1 text-slate-900 dark:text-slate-100"
          >
            <option value="" disabled>Selecione um canal...</option>
            <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
              {{ inbox.name }}
            </option>
          </select>
        </div>

        <!-- Assignee Select -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Atribuir Para
          </label>
          <select
            v-model="selectedAssigneeId"
            class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md bg-n-surface-1 text-slate-900 dark:text-slate-100"
          >
            <option :value="null">Ninguém (Não atribuído)</option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.name }}
            </option>
          </select>
        </div>

        <!-- Column Select -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            Etapa do Funil <span class="text-red-500">*</span>
          </label>
          <select
            v-model="selectedColumnLabel"
            class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md bg-n-surface-1 text-slate-900 dark:text-slate-100"
          >
            <option value="" disabled>Selecione a fase...</option>
            <option v-for="col in columns" :key="col.id" :value="col.label">
              {{ col.label }}
            </option>
          </select>
        </div>

        <!-- Error Message -->
        <p v-if="errorMessage" class="text-red-500 text-sm mt-2 font-medium">
          {{ errorMessage }}
        </p>
      </div>

      <div
        class="px-6 py-4 border-t border-n-weak flex justify-end items-center gap-2 bg-n-alpha-1"
      >
        <NextButton
          label="Cancelar"
          variant="faded"
          color="slate"
          @click="onClose"
        />
        <NextButton
          label="Criar Lead"
          icon="i-lucide-user-plus"
          :disabled="!isFormValid || isSaving"
          :is-loading="isSaving"
          @click="saveLead"
        />
      </div>
    </div>
  </woot-modal>
</template>
